import { addComponent, hasComponent, removeComponent } from "bitecs";
import * as THREE from "three";
import { TagTouchedComponent } from "../components/TagTouchedComponent";
import { TinyPetsWorld } from "../TinyPetsWorld";

export const createTouchSystem = (
    world: TinyPetsWorld,
    renderer: THREE.Renderer,
    camera: THREE.Camera,
    scene: THREE.Scene,
    config: { touchEndDelayMS: number; touchOffset: number; debug?: boolean }
): {
    touchSystem: (world: TinyPetsWorld) => TinyPetsWorld;
} => {
    /**
     * We register four raycasters, one centered and "correct" and three surrounding it in a slight offset.
     *
     * If any of the raycasters touch an object we assume it is touched. This way, touching something is less precise but
     * it is easier to touch things and an animation moving the model around does not make it flip out.
     */
    const raycasters = [
        new THREE.Raycaster(),
        new THREE.Raycaster(),
        new THREE.Raycaster(),
        new THREE.Raycaster(),
    ];

    let arrowHelpers: THREE.ArrowHelper[] = [];

    const pointer = new THREE.Vector2();

    const lastTouched: number[] = [];

    let touchedEntitiesOld: Set<number> = new Set();
    let touchedEntitiesNow: Set<number> = new Set();

    let touching = false;

    const onTouchStart = (event: PointerEvent) => {
        touching = true;
        onTouchMove(event);
    };

    const onTouchEnd = (event: PointerEvent) => {
        touching = false;
        onTouchMove(event);
    };

    const onTouchMove = (event: PointerEvent) => {
        touchedEntitiesNow = new Set();

        if (touching) {
            // calculate pointer position in normalized device coordinates
            // (-1 to +1) for both components

            var viewportOffset = renderer.domElement.getBoundingClientRect();

            pointer.x =
                ((event.clientX - viewportOffset.left) /
                    renderer.domElement.width) *
                    2 -
                1;
            pointer.y =
                -(
                    (event.clientY - viewportOffset.top) /
                    renderer.domElement.height
                ) *
                    2 +
                1;

            if (config.debug) {
                arrowHelpers.forEach((helper) => scene.remove(helper));

                arrowHelpers = raycasters.map(
                    (raycaster, i) =>
                        new THREE.ArrowHelper(
                            raycaster.ray.direction,
                            raycaster.ray.origin,
                            50,
                            (i / raycasters.length) * 0xffffff,
                            1,
                            1
                        )
                );

                arrowHelpers.forEach((helper) => scene.add(helper));
            }

            // update the picking ray with the camera and pointer position
            raycasters[0].setFromCamera(pointer, camera);
            raycasters[1].setFromCamera(
                {
                    x:
                        ((event.clientX - viewportOffset.left) /
                            renderer.domElement.width) *
                            2 -
                        1,
                    y:
                        -(
                            (event.clientY -
                                viewportOffset.top -
                                config.touchOffset) /
                            renderer.domElement.height
                        ) *
                            2 +
                        1,
                },
                camera
            );
            raycasters[2].setFromCamera(
                {
                    x:
                        ((event.clientX -
                            viewportOffset.left -
                            config.touchOffset) /
                            renderer.domElement.width) *
                            2 -
                        1,
                    y:
                        -(
                            (event.clientY -
                                viewportOffset.top +
                                config.touchOffset) /
                            renderer.domElement.height
                        ) *
                            2 +
                        1,
                },
                camera
            );
            raycasters[3].setFromCamera(
                {
                    x:
                        ((event.clientX -
                            viewportOffset.left +
                            config.touchOffset) /
                            renderer.domElement.width) *
                            2 -
                        1,
                    y:
                        -(
                            (event.clientY -
                                viewportOffset.top +
                                config.touchOffset) /
                            renderer.domElement.height
                        ) *
                            2 +
                        1,
                },
                camera
            );

            // calculate objects intersecting the picking ray
            const intersects = [
                ...raycasters[0].intersectObjects(scene.children),
                ...raycasters[1].intersectObjects(scene.children),
                ...raycasters[2].intersectObjects(scene.children),
                ...raycasters[3].intersectObjects(scene.children),
            ];

            for (let i = 0; i < intersects.length; i++) {
                let object = intersects[i].object;

                if (object.userData["eid"]) {
                    touchedEntitiesNow.add(object.userData["eid"]);
                }

                while (object.parent) {
                    object = object.parent;
                    if (object.userData["eid"]) {
                        touchedEntitiesNow.add(object.userData["eid"]);
                        continue;
                    }
                }
            }
        }
    };

    // make sure the dom element gets the events on all devices
    renderer.domElement.draggable = false;
    renderer.domElement.style.setProperty("touch-action", "none");

    renderer.domElement.addEventListener("pointerdown", onTouchStart);
    renderer.domElement.addEventListener("pointerup", onTouchEnd);
    renderer.domElement.addEventListener("pointerleave", onTouchEnd);

    renderer.domElement.addEventListener("pointermove", onTouchMove);

    return {
        touchSystem: (world: TinyPetsWorld) => {
            touchedEntitiesOld.forEach((eid) => {
                if (
                    hasComponent(world, TagTouchedComponent, eid) &&
                    !touchedEntitiesNow.has(eid) &&
                    world.time.elapsed - lastTouched[eid] >
                        config.touchEndDelayMS
                ) {
                    removeComponent(world, TagTouchedComponent, eid);
                }
            });

            touchedEntitiesNow.forEach((eid) => {
                if (!hasComponent(world, TagTouchedComponent, eid)) {
                    addComponent(world, TagTouchedComponent, eid);
                }

                lastTouched[eid] = world.time.elapsed;
            });

            touchedEntitiesOld = new Set([
                ...Array.from(touchedEntitiesNow),
                ...Object.keys(lastTouched).map(Number),
            ]);

            return world;
        },
    };
};
